import type { FC, PropsWithChildren } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Tooltip } from "@/components/Interface/Tooltip/Tooltip";
import { useGetFlatHomeInfo } from "@/services/api";
import { SimCardDetail } from "@/services/model";
import { hasWatchSimOrdered } from "@/utils/deviceUtils";
import { isFlatHomeTariff } from "@/utils/tariffUtils";

interface Props extends PropsWithChildren {
  simDetail: SimCardDetail;
}

type TariffChangeDisabledReasonsType = {
  watchSimStatusOrdered: boolean;
  orderInProcess: boolean;
};

/**
 * This tooltip lets the user know the reasons why their tariff option cannot
 * be changed.
 */
export const TariffChangeDisabledTooltip: FC<Props> = ({
  simDetail,
  children,
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.tariff",
  });

  const possibleReasons: TariffChangeDisabledReasonsType = {
    watchSimStatusOrdered: hasWatchSimOrdered(simDetail),
    orderInProcess: !!simDetail.servicePending,
  };
  const definiteReasonsAmount = Object.values(possibleReasons).filter(
    (reason) => reason,
  ).length;

  const { data: flatHomeInfoData } = useGetFlatHomeInfo(simDetail.simId ?? 0, {
    query: {
      enabled:
        simDetail.simId != undefined && isFlatHomeTariff(simDetail.serviceID),
    },
  });

  return (
    <Tooltip
      className="w-72"
      leftOnMobile
      content={
        isFlatHomeTariff(simDetail.serviceID) &&
        !flatHomeInfoData?.manageable ? (
          <>
            <h5 className="text-base font-semibold">
              {t("changeDisabledReasons.flatHome.title")}
            </h5>

            <p className="font-x-small mb-2 text-black">
              <Trans
                i18nKey="cockpit.managementTile.tariff.changeDisabledReasons.flatHome.text"
                values={{
                  date: new Date(
                    flatHomeInfoData?.manageableAt || "",
                  ).toLocaleDateString("de", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                  }),
                }}
              />
            </p>

            <p className="font-x-small text-black">
              {t("changeDisabledReasons.flatHome.hint")}
            </p>
          </>
        ) : (
          <>
            <h5 className="text-base font-semibold">
              {t("portal:common.tooltip.actionNotPossible.title")}
            </h5>

            <p className="font-x-small text-black">
              {t("portal:common.tooltip.actionNotPossible.text", {
                count: definiteReasonsAmount,
              })}
            </p>

            <ul className="font-x-small list-inside list-disc text-black">
              {possibleReasons.watchSimStatusOrdered && (
                <li>{t("changeDisabledReasons.watchSimNotActive")}</li>
              )}

              {possibleReasons.orderInProcess && (
                <li>{t("changeDisabledReasons.orderInProcess")}</li>
              )}
            </ul>
          </>
        )
      }
    >
      {children}
    </Tooltip>
  );
};
